import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(", ");
  return String(detail);
}

export default function AdminLogin() {
  const [step, setStep] = useState("password"); // password | mfa
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.mfa_required) {
        setChallenge(data.challenge_token);
        setStep("mfa");
        toast.message("Enter the 6-digit code from your authenticator app");
      } else {
        await refresh();
        toast.success("Welcome back.");
        nav("/admin");
      }
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/mfa/verify", { challenge_token: challenge, code: otp });
      await refresh();
      toast.success("MFA verified.");
      nav("/admin");
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Bad code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111] text-white flex items-center justify-center p-6 sm:p-10 relative overflow-hidden">
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-bold">
        <ArrowLeft size={16} /> Back to site
      </Link>
      <img src={LOGO_URL} alt="B-Raw" className="absolute top-6 right-6 h-10 w-auto opacity-90" />

      {step === "password" ? (
        <form onSubmit={submitPassword} data-testid="admin-login-form" className="w-full max-w-sm">
          <div className="h-12 w-12 rounded-xl bg-[#F2A900] text-[#111] flex items-center justify-center"><Lock /></div>
          <h1 className="mt-6 font-display font-black text-4xl">Owner Console.</h1>
          <p className="mt-2 text-white/70 text-sm">Sign in to manage B-Raw.</p>

          <div className="mt-8">
            <Label className="text-xs uppercase tracking-widest text-white/60">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" className="mt-1 h-12 bg-white/5 border-white/20 text-white focus-visible:ring-[#F2A900]" />
          </div>
          <div className="mt-4">
            <Label className="text-xs uppercase tracking-widest text-white/60">Password</Label>
            <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" className="mt-1 h-12 bg-white/5 border-white/20 text-white focus-visible:ring-[#F2A900]" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit" className="mt-8 w-full h-12 bg-[#F2A900] hover:bg-[#e09b00] text-[#111] font-bold rounded-full">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      ) : (
        <form onSubmit={submitOtp} data-testid="mfa-form" className="w-full max-w-sm">
          <div className="h-12 w-12 rounded-xl bg-[#F2A900] text-[#111] flex items-center justify-center"><ShieldCheck /></div>
          <h1 className="mt-6 font-display font-black text-4xl">Two-factor.</h1>
          <p className="mt-2 text-white/70 text-sm">Enter the 6-digit code from your authenticator app.</p>
          <div className="mt-8">
            <Label className="text-xs uppercase tracking-widest text-white/60">Code</Label>
            <Input required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" pattern="[0-9]{6}" data-testid="mfa-code" className="mt-1 h-14 text-2xl tracking-[0.5em] text-center bg-white/5 border-white/20 text-white focus-visible:ring-[#F2A900]" />
          </div>
          <Button type="submit" disabled={loading || otp.length !== 6} data-testid="mfa-submit" className="mt-8 w-full h-12 bg-[#F2A900] hover:bg-[#e09b00] text-[#111] font-bold rounded-full">
            {loading ? "Verifying…" : "Verify"}
          </Button>
          <button type="button" onClick={() => setStep("password")} className="mt-4 text-sm text-white/60 hover:text-white">← Back</button>
        </form>
      )}
    </div>
  );
}
